import { Pump } from "../../dtos/pump";
import { Field } from "../../dtos/field";
import { Sensor } from "../../dtos/sensor";
import { Pivot } from "../../dtos/pivot";
import { Bounds } from "google-map-react";
import { useMemo } from "react";
import useSupercluster from "use-supercluster";
import { MarkerCategory } from "../../enums/markerCategory";
import { HomeCard } from "../../enums/homeCard";
import { UnitState } from "../../enums/unitState";

const SAMPLE_VALVES = [
  { id: 1, name: "Valve 1", serialNumber: 10001 },
  { id: 2, name: "Valve 2", serialNumber: 10002 },
  { id: 3, name: "Valve 3", serialNumber: 10003 },
  { id: 4, name: "Valve 4", serialNumber: 10004 },
  { id: 5, name: "Valve 5", serialNumber: 10005 }
];

const getPivotValveDetails = (pivot: Pivot) =>
  (pivot.valve_ids ?? [])
    .map((valveId, index) => {
      const valve = SAMPLE_VALVES.find(item => item.id === valveId);

      return valve ? {
        id: valve.id,
        name: valve.name,
        state: index % 2 === 0 ? "Open" : "Closed"
      } : null;
    })
    .filter(Boolean);

const getPivotPumpDetails = (pivot: Pivot) =>
  (pivot.pump_locations ?? [])
    .map(location => {
      return {
        id: location.id,
        name: location.name ?? `Pump ${location.id}`,
        state: pivot.online ? "Running" : "Stopped"
      };
    })
    .filter(Boolean);

const VALVE_OFFSETS = [
  { lat: 0.00008, lon: 0.00032 },
  { lat: 0.0003, lon: 0.00008 },
  { lat: -0.0001, lon: 0.0003 },
  { lat: -0.0003, lon: 0.00012 },
  { lat: 0.00004, lon: -0.00032 }
];

const buildPivotEquipmentMarkers = (pivot: Pivot, showValves: boolean) => {
  if (pivot.lat == null || pivot.lon == null) {
    return [];
  }

  const pivotLat = pivot.lat;
  const pivotLon = pivot.lon;

  const pumpMarkers = (pivot.pump_locations ?? []).map(location => {
    return {
      type: "Feature",
      properties: {
        cluster: false,
        id: pivot.id * 100 + location.id,
        serialNumber: location.serialNumber ?? location.id,
        name: location.name ?? `Pump ${location.id}`,
        category: MarkerCategory.Pump,
        type: 1,
        unitState: pivot.online ? UnitState.Running : UnitState.Idle,
        data4: null
      },
      geometry: {
        type: "Point",
        coordinates: [location.lon, location.lat]
      }
    };
  }).filter(Boolean);

  const valveMarkers = !showValves ? [] : (pivot.valve_ids ?? []).map((valveId: number, index: number) => {
    const valve = SAMPLE_VALVES.find(item => item.id === valveId);
    const storedLocation = pivot.valve_locations?.find(location => location.id === valveId);
    const offset = VALVE_OFFSETS[index % VALVE_OFFSETS.length];
    const isOpen = index % 2 === 0;
    const markerLat = storedLocation?.lat ?? (pivotLat + offset.lat);
    const markerLon = storedLocation?.lon ?? (pivotLon + offset.lon);

    return valve ? {
      type: "Feature",
      properties: {
        cluster: false,
        id: pivot.id * 1000 + valve.id,
        serialNumber: valve.serialNumber,
        name: valve.name,
        category: MarkerCategory.Field,
        type: 98,
        online: false,
        currentEngData: isOpen ? 100 : 0
      },
      geometry: {
        type: "Point",
        coordinates: [markerLon, markerLat]
      }
    } : null;
  }).filter(Boolean);

  return [...pumpMarkers, ...valveMarkers];
  // return [...valveMarkers];
};

export const useClusteredPumps = ({
  pumpsWithCoords,
  sensorsWithCoords,
  fieldsWithCoords,
  pivotsWithCoords,
  selectedPivotId,
  mapZoom,
  bounds,
  cardType
}: {
  pumpsWithCoords: Pump[];
  fieldsWithCoords: Field[];
  sensorsWithCoords: Sensor[];
  pivotsWithCoords: Pivot[];
  selectedPivotId: number | null;
  mapZoom: number;
  bounds: Bounds | null;
  cardType: HomeCard
}) => {
  const clusterPoints = useMemo(
    () => {
      if (cardType === HomeCard.Pumps) {
        return [
          ...pumpsWithCoords.map(p => ({
            type: "Feature",
            properties: {
              cluster: false,
              id: p.id,
              serialNumber: p.serialNumber,
              name: p.name,
              category: MarkerCategory.Pump,
              type: p.type,
              unitState: p.unitState
            },
            geometry: {
              type: "Point",
              coordinates: [p.lon, p.lat]
            }
          }))
        ];
      }
      else if (cardType === HomeCard.Fields) {
        return [
          ...fieldsWithCoords.map(f => ({
            type: "Feature",
            properties: {
              cluster: false,
              id: f.id,
              serialNumber: f.valveSerialNumber,
              name: f.name,
              category: MarkerCategory.Field,
              type: 98,
              online: f.online ? true : false,
              currentEngData: f.position

            },
            geometry: {
              type: "Point",
              coordinates: [f.lon, f.lat]
            }
          }))
        ];
      }
      else if (cardType === HomeCard.Pivots) {
        return [
          ...pivotsWithCoords.map(p => ({
            type: "Feature",
            properties: {
              cluster: false,
              id: p.id,
              serialNumber: p.id,
              name: p.name,
              category: MarkerCategory.Pivot,
              type: 97,
              online: p.online,
              status: p.status,
              associatedValves: getPivotValveDetails(p),
              associatedPumps: getPivotPumpDetails(p)
            },
            geometry: {
              type: "Point",
              coordinates: [p.lon!, p.lat!]
            }
          })),
          ...pivotsWithCoords.flatMap(p => buildPivotEquipmentMarkers(p, p.id === selectedPivotId))
        ];
      }
      else if (cardType === HomeCard.Sensors) {
        return [
          ...sensorsWithCoords.map(s => ({
            type: "Feature",
            properties: {
              cluster: false,
              id: s.id,
              serialNumber: s.serial,
              name: s.name,
              category: MarkerCategory.Sensor,
              type: 99,
              status: s.alarmStatus,
              online: s.online ? true : false,
              currentEngData: s.currentEngData
            },
            geometry: {
              type: "Point",
              coordinates: [s.lon, s.lat]
            }
          }))
        ];
      }
      else if (cardType === HomeCard.PumpWeather) {
        return [
          ...pumpsWithCoords.filter(p => p.weatherLink).map(p => ({
            type: "Feature",
            properties: {
              cluster: false,
              id: p.id,
              serialNumber: p.serialNumber,
              name: p.name,
              category: MarkerCategory.Pump,
              type: p.type,
              unitState: p.unitState
            },
            geometry: {
              type: "Point",
              coordinates: [p.lon, p.lat]
            }
          }))
        ];
      }
      else {
        return [
          ...pumpsWithCoords.map(p => ({
            type: "Feature",
            properties: {
              cluster: false,
              id: p.id,
              serialNumber: p.serialNumber,
              name: p.name,
              category: MarkerCategory.Pump,
              type: p.type,
              unitState: p.unitState
            },
            geometry: {
              type: "Point",
              coordinates: [p.lon, p.lat]
            }
          })),
          ...fieldsWithCoords.map(f => ({
            type: "Feature",
            properties: {
              cluster: false,
              id: f.id,
              serialNumber: f.valveSerialNumber,
              name: f.name,
              category: MarkerCategory.Field,
              type: 98,
              online: f.online ? true : false,
              currentEngData: f.position

            },
            geometry: {
              type: "Point",
              coordinates: [f.lon, f.lat]
            }
          })),
          ...sensorsWithCoords.map(s => ({
            type: "Feature",
            properties: {
              cluster: false,
              id: s.id,
              serialNumber: s.serial,
              name: s.name,
              category: MarkerCategory.Sensor,
              type: 99,
              status: s.alarmStatus,
              online: s.online ? true : false,
              currentEngData: s.currentEngData
            },
            geometry: {
              type: "Point",
              coordinates: [s.lon, s.lat]
            }
          }))
        ]
      }
    },
    [pumpsWithCoords, fieldsWithCoords, sensorsWithCoords, pivotsWithCoords, selectedPivotId, cardType]
  );
  const { clusters } = useSupercluster({
    points: clusterPoints,
    zoom: mapZoom,
    bounds: bounds
      ? [bounds.nw.lng, bounds.se.lat, bounds.se.lng, bounds.nw.lat]
      : [],
    options: { radius: 75, maxZoom: 20 }
  });
  return cardType === HomeCard.Pivots ? clusterPoints : clusters;
};
